import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Snackbar,
} from '@mui/material';
import MainLayout from '../../layout/MainLayout';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { keyframes } from '@mui/system';

// Custom hooks and components
import useDataCatalogState from './data_catalog/hooks/useDataCatalogState';
import useFilterSort from './data_catalog/hooks/useFilterSort';
import useWorkingLists from './data_catalog/hooks/useWorkingLists';
import DataCatalogTable from './data_catalog/components/DataCatalogTable';
import CompactFilterBar from './data_catalog/components/CompactFilterBar';
import FilterExpansionPanel from './data_catalog/components/FilterExpansionPanel';
import WorkingListDialog from './data_catalog/components/WorkingListDialog';
import ListAddRequestDialog from './data_catalog/components/ListAddRequestDialog';
import ListManagementDialog from './data_catalog/components/ListManagementDialog';
import AIAssistantDialog from './data_catalog/components/AIAssistantDialog';
import TagManagementDialog from './data_catalog/components/TagManagementDialog';
import SetManagementDialog from './data_catalog/components/SetManagementDialog';
import TagDeletionRequestDialog from './data_catalog/components/TagDeletionRequestDialog';
import { getOMOPStatusColor, getOMOPStatusLabel, getPrimaryName, tagIconOptions as importedTagIconOptions, getIconComponent as importedGetIconComponent } from './data_catalog/utils/helpers';

// Data imports
import parametersData from '../../data/parameters.json';
import stationsData from '../../data/stations.json';
import savedSetsData from '../../data/savedSets.json';
import tagStatsData from '../../data/tagStats.json';
import aiSuggestionsData from '../../data/aiSuggestions.json';

// Subtle pulse animation for tags with deletion requests
const subtlePulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(237, 108, 2, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(237, 108, 2, 0.1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(237, 108, 2, 0);
  }
`;

// Use imported versions from helpers.js
const tagIconOptions = importedTagIconOptions;
const getIconComponent = importedGetIconComponent;

const DataCatalog = () => {

  // Current user (mock - in real app would come from auth context)
  const currentUser = 'Max Mustermann';

  // Use custom hooks for state management
  const state = useDataCatalogState();
  const workingLists = useWorkingLists(currentUser);

  // Destructure state for easier access
  const {
    searchTerm, setSearchTerm,
    nameView, setNameView,
    showFilters, setShowFilters,
    expandedRows, setExpandedRows,
    selectedTags, setSelectedTags,
    selectedStations, setSelectedStations,
    selectedSensitivity, setSelectedSensitivity,
    selectedOmopStatus, setSelectedOmopStatus,
    selectedSourceSystems, setSelectedSourceSystems,
    filterPanelOpen, setFilterPanelOpen,
    orderBy, setOrderBy,
    order, setOrder,
    tagDialogOpen, setTagDialogOpen,
    editingParameterId, setEditingParameterId,
    newTagInput, setNewTagInput,
    listDialogOpen, setListDialogOpen,
    selectedParameterForList, setSelectedParameterForList,
    listManagementDialogOpen, setListManagementDialogOpen,
    setManagementDialogOpen, setSetManagementDialogOpen,
    activeSetTab, setActiveSetTab,
    createTagPrivate, setCreateTagPrivate,
    selectedTagIcon, setSelectedTagIcon,
    deletionRequestDialog, setDeletionRequestDialog,
    activeWorkingList, setActiveWorkingList,
    showOnlyActiveListParams, setShowOnlyActiveListParams,
    listComparisonFilter, setListComparisonFilter,
    listAddRequestDialog, setListAddRequestDialog,
    aiAssistantOpen, setAiAssistantOpen,
    studyDescription, setStudyDescription,
    studyType, setStudyType,
    specialty, setSpecialty,
    aiSuggestions, setAiSuggestions,
    selectedSuggestions, setSelectedSuggestions,
    isGeneratingSuggestions, setIsGeneratingSuggestions,
    aiSuggestedParameterIds, setAiSuggestedParameterIds,
    setParameterIds, setSetParameterIds,
    snackbar, setSnackbar,
    favorites, setFavorites,
    handleRowClick,
    isRowExpanded,
    handleTagToggle,
    handleStationToggle,
    handleSourceSystemToggle,
    clearAllFilters,
    toggleFavorite,
  } = state;

  // Destructure working list functions
  const {
    parameterLists,
    setParameterLists,
    isParameterInAnyList,
    isParameterInActiveList,
    hasParameterPendingInActiveList,
    isParameterInList,
    hasParameterPendingApproval,
    handleAddToList: addToList,
    handleRemoveFromList: removeFromList,
    handleToggleParameterInList,
  } = workingLists;

  // Use imported parameters data from JSON
  const mockParameters = parametersData.parameters;

  // Tag statistics from JSON
  const allTagsWithStats = tagStatsData;

  const allTags = allTagsWithStats.map(t => t.name);
  const popularTags = allTagsWithStats.slice(0, 10).map(t => t.name); // Top 10

  // Station data from JSON
  const allStationsWithCategories = stationsData.stations;
  const stationCategories = stationsData.categories;
  const allStations = allStationsWithCategories.map(s => s.name);
  const popularStations = stationsData.popularStations;

  // Source systems - extract unique systems from parameters
  const allSourceSystems = [...new Set(mockParameters.map(p => p.sourceSystem).filter(Boolean))];

  // Saved filter sets from JSON
  const savedSets = savedSetsData;

  // parameterLists state is now managed by useWorkingLists hook
  // Use custom hook for filtering and sorting
  const {
    filteredParameters,
    sortedParameters,
    getParameterListStatus,
    getListComparisonCounts,
  } = useFilterSort({
    parameters: mockParameters,
    searchTerm,
    selectedTags,
    selectedStations,
    selectedSensitivity,
    selectedOmopStatus,
    selectedSourceSystems,
    aiSuggestedParameterIds,
    setParameterIds,
    showOnlyActiveListParams,
    activeWorkingList,
    listComparisonFilter,
    orderBy,
    order,
    parameterLists,
    isParameterInActiveList,
  });

  // Enrich parameters with favorite status
  const parametersWithFavorites = sortedParameters.map(param => ({
    ...param,
    isFavorite: favorites.includes(param.id)
  }));

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // handleTagToggle is already provided by useDataCatalogState hook

  const handleFavoriteToggle = (parameterId, event) => {
    event?.stopPropagation(); // Prevent row click
    toggleFavorite(parameterId);
    // In real app, this would call API
    console.log('Toggle favorite for parameter:', parameterId);
  };

  // List Management Handlers
  const handleOpenListDialog = (parameterId, event) => {
    event?.stopPropagation(); // Prevent row click

    // Quick-add: If exactly one active working list is set, add directly without dialog
    if (parameterLists && activeWorkingList.length === 1) {
      const list = parameterLists.find(l => l.id === activeWorkingList[0]);
      if (list) {
        const parameter = mockParameters.find(p => p.id === parameterId);
        const isAlreadyInList = list.parameterIds.includes(parameterId);
        const hasPendingRequest = list.additionRequests.some(req => req.parameterId === parameterId);

        if (isAlreadyInList) {
          // Show message that it's already in the list
          setSnackbar({
            open: true,
            message: `Parameter "${parameter?.name}" ist bereits in "${list.name}"`,
            severity: 'info'
          });
          return;
        }

        if (hasPendingRequest) {
          // Show message that request is already pending
          setSnackbar({
            open: true,
            message: `Vorschlag für "${parameter?.name}" in "${list.name}" ist bereits ausstehend`,
            severity: 'info'
          });
          return;
        }

        // Private list: Add directly
        if (list.isPrivate && list.createdBy === currentUser) {
          console.log('Quick-add parameter', parameterId, 'to private list', list.name);
          // In real app: API call to add parameter to list

          setSnackbar({
            open: true,
            message: `Parameter "${parameter?.name}" zu "${list.name}" hinzugefügt`,
            severity: 'success'
          });
          return;
        }

        // Public list: Send approval request
        if (!list.isPrivate) {
          console.log('Send approval request for parameter', parameterId, 'to public list', list.name);
          // In real app: API call to create approval request

          setSnackbar({
            open: true,
            message: `Vorschlag für "${parameter?.name}" in "${list.name}" gesendet (Approval erforderlich)`,
            severity: 'warning'
          });
          return;
        }
      }
      return;
    }

    // Normal behavior: Open dialog
    setSelectedParameterForList(parameterId);
    setListDialogOpen(true);
  };

  const handleRemoveFromList = (parameterId, listId, event) => {
    event?.stopPropagation();

    if (!parameterLists) return;

    const list = parameterLists.find(l => l.id === listId);
    const parameter = mockParameters.find(p => p.id === parameterId);

    if (list && parameter) {
      // Update state: Remove parameter from list
      setParameterLists(prevLists =>
        prevLists.map(l =>
          l.id === listId
            ? { ...l, parameterIds: l.parameterIds.filter(id => id !== parameterId) }
            : l
        )
      );

      setSnackbar({
        open: true,
        message: `"${parameter.name}" aus "${list.name}" entfernt`,
        severity: 'success'
      });
    }
  };

  const handleAddToList = (parameterId, listId, event) => {
    event?.stopPropagation();

    if (!parameterLists) return;

    const list = parameterLists.find(l => l.id === listId);
    const parameter = mockParameters.find(p => p.id === parameterId);

    if (list && parameter) {
      // Check if already in list
      if (list.parameterIds.includes(parameterId)) {
        setSnackbar({
          open: true,
          message: `"${parameter.name}" ist bereits in "${list.name}"`,
          severity: 'info'
        });
        return;
      }

      // Update state: Add parameter to list
      setParameterLists(prevLists =>
        prevLists.map(l =>
          l.id === listId
            ? { ...l, parameterIds: [...l.parameterIds, parameterId] }
            : l
        )
      );

      setSnackbar({
        open: true,
        message: `"${parameter.name}" zu "${list.name}" hinzugefügt`,
        severity: 'success'
      });
    }
  };

  // Working list functions are now provided by useWorkingLists hook
  // List comparison and filter functions are now provided by useFilterSort hook

  const handleConfirmListAdditionRequest = () => {
    const { list, parameter } = listAddRequestDialog;
    if (!list || !parameter) return;

    console.log('Request to add parameter', parameter, 'to public list', list.name);
    // In real app: API call to add addition request
    // List owner or data stewards can approve

    setListAddRequestDialog({ open: false, list: null, parameter: null });
  };

  // handleRowClick and isRowExpanded are now provided by useDataCatalogState hook

  const handleAddTagToParameter = (parameterId, tagName, isPrivate = createTagPrivate, icon = null) => {
    // In real app, this would call API
    console.log('Add tag to parameter:', parameterId, tagName, 'isPrivate:', isPrivate, 'icon:', icon);
    // Check if tag exists
    const existingTag = allTagsWithStats.find(t => t.name === tagName);
    if (!existingTag) {
      // Create new tag
      console.log('Creating new tag:', tagName, 'as', isPrivate ? 'private' : 'public', 'with icon:', icon);
      // In real app: API call to create tag with privacy setting and icon
    }
    // Mock: This would update the parameter's tags in state
  };

  const handleRemoveTagFromParameter = (parameterId, tagName) => {
    const tag = allTagsWithStats.find(t => t.name === tagName);

    if (!tag) {
      console.log('Tag not found:', tagName);
      return;
    }

    // If private tag and user is owner, allow direct deletion
    if (tag.isPrivate && tag.createdBy === currentUser) {
      console.log('Remove private tag from parameter:', parameterId, tagName);
      // In real app: Direct API call to remove tag
      return;
    }

    // If public tag, require approval process
    if (!tag.isPrivate) {
      setDeletionRequestDialog({ open: true, tag, parameterId });
      return;
    }

    // If trying to remove someone else's private tag (shouldn't happen in UI)
    console.log('Cannot remove private tag from another user');
  };

  const handleCreateNewTag = (tagName, isPrivate = createTagPrivate) => {
    // In real app, this would call API to create tag
    console.log('Create new tag:', tagName, 'isPrivate:', isPrivate, 'createdBy:', currentUser);
    // Mock: This would add the tag to allTags
  };

  const handleMakeTagPublic = (tagName) => {
    const tag = allTagsWithStats.find(t => t.name === tagName);
    if (tag && tag.isPrivate && tag.createdBy === currentUser) {
      console.log('Make tag public:', tagName);
      // In real app: API call to update tag privacy
    }
  };

  const handleRequestTagDeletion = (tagName, parameterId) => {
    console.log('Request deletion of public tag:', tagName, 'for parameter:', parameterId);
    // In real app: API call to add deletion request
    // If second user confirms, tag gets removed
  };

  const handleConfirmDeletionRequest = () => {
    const { tag, parameterId } = deletionRequestDialog;
    if (!tag) return;

    // Data Stewards can remove tags directly
    console.log('Removing tag from parameter:', parameterId);
    // In real app: API call to remove tag

    setDeletionRequestDialog({ open: false, tag: null });
  };

  const handleLoadSet = (set) => {
    console.log('Loading set:', set.name);

    // Load tags if present
    if (set.items.tags && set.items.tags.length > 0) {
      setSelectedTags(prev => {
        const newTags = set.items.tags.filter(tag => !prev.includes(tag));
        return [...prev, ...newTags];
      });
    }

    // Load stations if present
    if (set.items.stations && set.items.stations.length > 0) {
      setSelectedStations(prev => {
        const newStations = set.items.stations.filter(station => !prev.includes(station));
        return [...prev, ...newStations];
      });
    }

    // Load parameters if present - use setParameterIds for saved sets
    if (set.items.parameterIds && set.items.parameterIds.length > 0) {
      setSetParameterIds(prev => {
        const newIds = set.items.parameterIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }

    // Show success message
    setSnackbar({
      open: true,
      message: `Set "${set.name}" wurde angewendet`,
      severity: 'success'
    });
  };

  const getSetTypeBadge = (type) => {
    switch (type) {
      case 'parameters': return 'PARAM';
      case 'stations': return 'STAT';
      case 'tags': return 'TAG';
      case 'mixed': return 'MIX';
      default: return 'SET';
    }
  };

  const getOMOPStatusColor = (status) => {
    switch (status) {
      case 'validated': return 'success';
      case 'mapped': return 'info';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  // AI Assistant Handlers
  const handleGenerateSuggestions = () => {
    setIsGeneratingSuggestions(true);

    // Simulate AI processing
    setTimeout(() => {
      // Use AI suggestions from JSON data
      setAiSuggestions(aiSuggestionsData);
      // Pre-select all suggestions
      setSelectedSuggestions(aiSuggestionsData.map(s => s.id));
      setIsGeneratingSuggestions(false);
    }, 2000);
  };

  const handleApplySuggestionsAsFilter = () => {
    // Only use selected suggestions, not all
    const parameterIds = selectedSuggestions;

    // Set as filter
    setAiSuggestedParameterIds(parameterIds);

    // Show success message
    setSnackbar({
      open: true,
      message: `${parameterIds.length} KI-vorgeschlagene Parameter als Filter angewendet`,
      severity: 'success'
    });

    // Close dialog and reset
    setAiAssistantOpen(false);
    setStudyDescription('');
    setStudyType('');
    setSpecialty('');
    setAiSuggestions([]);
    setSelectedSuggestions([]);
  };

  const getOMOPStatusLabel = (status) => {
    switch (status) {
      case 'validated': return 'Validiert';
      case 'mapped': return 'Gemappt';
      case 'pending': return 'Ausstehend';
      default: return 'Unbekannt';
    }
  };

  const getPrimaryName = (param) => {
    if (nameView === 'omop') return param.omopName || param.name;
    return param.name;
  };

  const getSecondaryName = (param) => {
    if (nameView === 'both') {
      if (nameView === 'omop') return param.name;
      return param.omopName;
    }
    return null;
  };

  const getPrimaryDescription = (param) => {
    if (nameView === 'omop') return param.omopDescription || param.description;
    return param.description;
  };

  // Utility function to get parameter name by ID
  const getParameterName = (paramId) => {
    const param = mockParameters.find(p => p.id === paramId);
    return param ? param.name : `Parameter ${paramId}`;
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Datenkatalog
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {filteredParameters.length} Parameter gefunden
          </Typography>
        </Box>

        {/* Compact Filter Bar with integrated search */}
        <CompactFilterBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          savedSets={savedSets}
          handleLoadSet={handleLoadSet}
          getSetTypeBadge={getSetTypeBadge}
          selectedTags={selectedTags}
          selectedStations={selectedStations}
          selectedSensitivity={selectedSensitivity}
          selectedOmopStatus={selectedOmopStatus}
          selectedSourceSystems={selectedSourceSystems}
          aiSuggestedParameterIds={aiSuggestedParameterIds}
          setParameterIds={setParameterIds}
          setSelectedTags={setSelectedTags}
          setSelectedStations={setSelectedStations}
          setSelectedSensitivity={setSelectedSensitivity}
          setSelectedOmopStatus={setSelectedOmopStatus}
          setSelectedSourceSystems={setSelectedSourceSystems}
          setAiSuggestedParameterIds={setAiSuggestedParameterIds}
          setSetParameterIds={setSetParameterIds}
          parameters={mockParameters}
          getParameterName={getParameterName}
          filterPanelOpen={filterPanelOpen}
          setFilterPanelOpen={setFilterPanelOpen}
          setAiAssistantOpen={setAiAssistantOpen}
          nameView={nameView}
          setNameView={setNameView}
          clearAllFilters={clearAllFilters}
        />

        {/* Filter Expansion Panel */}
        <FilterExpansionPanel
          open={filterPanelOpen}
          onClose={() => setFilterPanelOpen(false)}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          selectedStations={selectedStations}
          setSelectedStations={setSelectedStations}
          selectedSensitivity={selectedSensitivity}
          setSelectedSensitivity={setSelectedSensitivity}
          selectedOmopStatus={selectedOmopStatus}
          setSelectedOmopStatus={setSelectedOmopStatus}
          selectedSourceSystems={selectedSourceSystems}
          setSelectedSourceSystems={setSelectedSourceSystems}
          allSourceSystems={allSourceSystems}
          handleSourceSystemToggle={handleSourceSystemToggle}
          allTags={allTags}
          popularTags={popularTags}
          allStations={allStations}
          allStationsWithCategories={allStationsWithCategories}
          popularStations={popularStations}
          savedSets={savedSets}
          handleLoadSet={handleLoadSet}
          handleTagToggle={handleTagToggle}
          setTagDialogOpen={setTagDialogOpen}
        />

        {/* Active Working List Selector */}
        <Paper sx={{ p: 2, mb: 2, backgroundColor: activeWorkingList.length > 0 ? '#f0f7ff' : 'background.paper', border: activeWorkingList.length > 0 ? '2px solid' : 'none', borderColor: 'primary.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
              {activeWorkingList.length > 0 ? (
                <PlaylistAddCheckIcon color="primary" />
              ) : (
                <PlaylistAddIcon color="action" />
              )}
              <Typography variant="subtitle2" fontWeight="bold" color={activeWorkingList.length > 0 ? 'primary' : 'textSecondary'}>
                Aktive Arbeitsliste{activeWorkingList.length === 2 ? 'n' : ''}:
              </Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 250, flexGrow: 1 }}>
              <Select
                multiple
                value={activeWorkingList}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limit to max 2 selections
                  if (Array.isArray(value) && value.length <= 2) {
                    setActiveWorkingList(value);
                  }
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <em style={{ color: '#999' }}>Keine Liste ausgewählt (max. 2 für Vergleich)</em>;
                  }
                  const selectedLists = parameterLists ? selected.map(id => parameterLists.find(l => l.id === id)).filter(Boolean) : [];
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {selectedLists.map((list, idx) => (
                        <Box key={list.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: idx === 0 ? '#e3f2fd' : '#e8f5e9', px: 1, py: 0.5, borderRadius: 1 }}>
                          {selectedLists.length >= 1 && (
                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: idx === 0 ? '#2196f3' : '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.7rem', fontWeight: 'bold', mr: 0.5 }}>
                              {idx === 0 ? 'A' : 'B'}
                            </Box>
                          )}
                          {list.isPrivate ? <LockIcon fontSize="small" /> : <PublicIcon fontSize="small" />}
                          <Typography variant="body2" fontWeight="bold">{list.name}</Typography>
                          <Chip label={`${list.parameterIds.length}`} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                        </Box>
                      ))}
                    </Box>
                  );
                }}
              >
                <MenuItem value="" disabled sx={{ opacity: 1, fontStyle: 'italic', fontSize: '0.75rem', color: '#999', py: 1 }}>
                  {activeWorkingList.length === 0 ? 'Wählen Sie bis zu 2 Listen zum Vergleichen' : activeWorkingList.length === 1 ? 'Wählen Sie eine weitere Liste zum Vergleichen' : 'Maximum erreicht (2 Listen)'}
                </MenuItem>
                <MenuItem value="" disabled sx={{ opacity: 1, fontWeight: 'bold', fontSize: '0.75rem', color: '#666', py: 0.5 }}>
                  ─── PRIVATE LISTEN ───
                </MenuItem>
                {parameterLists
                  .filter(list => list.isPrivate && list.createdBy === currentUser)
                  .map(list => (
                    <MenuItem
                      key={list.id}
                      value={list.id}
                      sx={{ pl: 3 }}
                      disabled={activeWorkingList.length >= 2 && !activeWorkingList.includes(list.id)}
                    >
                      <Checkbox
                        checked={activeWorkingList.includes(list.id)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      🔒 {list.name} {list.isDefault && '⭐'} ({list.parameterIds.length})
                    </MenuItem>
                  ))}
                <MenuItem value="" disabled sx={{ opacity: 1, fontWeight: 'bold', fontSize: '0.75rem', color: '#666', py: 0.5, mt: 1 }}>
                  ─── ÖFFENTLICHE LISTEN ───
                </MenuItem>
                {parameterLists
                  .filter(list => !list.isPrivate)
                  .map(list => (
                    <MenuItem
                      key={list.id}
                      value={list.id}
                      sx={{ pl: 3 }}
                      disabled={activeWorkingList.length >= 2 && !activeWorkingList.includes(list.id)}
                    >
                      <Checkbox
                        checked={activeWorkingList.includes(list.id)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      🌐 {list.name} ✓ ({list.parameterIds.length})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {activeWorkingList.length > 0 && (
              <Button
                size="small"
                startIcon={<CloseIcon />}
                onClick={() => {
                  setActiveWorkingList([]);
                  setShowOnlyActiveListParams(false);
                  setListComparisonFilter('all');
                }}
                variant="outlined"
              >
                Deaktivieren
              </Button>
            )}

            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setListManagementDialogOpen(true)}
              variant="outlined"
            >
              Alle Listen verwalten
            </Button>

            {activeWorkingList.length === 1 && (
              <Chip
                icon={showOnlyActiveListParams ? <CheckCircleIcon /> : <FilterListIcon />}
                label={showOnlyActiveListParams ? "Nur in aktiver Liste" : "Alle Parameter"}
                onClick={() => setShowOnlyActiveListParams(!showOnlyActiveListParams)}
                color={showOnlyActiveListParams ? "success" : "default"}
                variant={showOnlyActiveListParams ? "filled" : "outlined"}
                sx={{ ml: 'auto' }}
              />
            )}
          </Box>

          {/* Filter Chips for List Comparison */}
          {activeWorkingList.length === 2 && parameterLists && (() => {
            const counts = getListComparisonCounts();
            const listA = parameterLists.find(l => l.id === activeWorkingList[0]);
            const listB = parameterLists.find(l => l.id === activeWorkingList[1]);

            return (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" fontWeight="bold" color="textSecondary">
                  Vergleich:
                </Typography>
                <Chip
                  label={`Alle (${filteredParameters.length})`}
                  onClick={() => setListComparisonFilter('all')}
                  color={listComparisonFilter === 'all' ? 'primary' : 'default'}
                  variant={listComparisonFilter === 'all' ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  icon={<Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#2196f3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.7rem', fontWeight: 'bold' }}>A</Box>}
                  label={`Nur A: ${listA?.name} (${counts?.list1Only || 0})`}
                  onClick={() => setListComparisonFilter('list1Only')}
                  color={listComparisonFilter === 'list1Only' ? 'primary' : 'default'}
                  variant={listComparisonFilter === 'list1Only' ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    '& .MuiChip-icon': {
                      color: '#fff !important'
                    }
                  }}
                />
                <Chip
                  icon={<Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.7rem', fontWeight: 'bold' }}>B</Box>}
                  label={`Nur B: ${listB?.name} (${counts?.list2Only || 0})`}
                  onClick={() => setListComparisonFilter('list2Only')}
                  color={listComparisonFilter === 'list2Only' ? 'primary' : 'default'}
                  variant={listComparisonFilter === 'list2Only' ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    '& .MuiChip-icon': {
                      color: '#fff !important'
                    }
                  }}
                />
                <Chip
                  label={`A ∩ B (${counts?.both || 0})`}
                  onClick={() => setListComparisonFilter('both')}
                  color={listComparisonFilter === 'both' ? 'secondary' : 'default'}
                  variant={listComparisonFilter === 'both' ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  label={`Keine (${counts?.neither || 0})`}
                  onClick={() => setListComparisonFilter('neither')}
                  color={listComparisonFilter === 'neither' ? 'default' : 'default'}
                  variant={listComparisonFilter === 'neither' ? 'filled' : 'outlined'}
                  size="small"
                />
              </Box>
            );
          })()}

          {activeWorkingList.length === 1 && parameterLists && (
            <Alert
              severity={parameterLists.find(l => l.id === activeWorkingList[0])?.isPrivate ? "info" : "warning"}
              sx={{ mt: 2 }}
              icon={<CheckCircleIcon />}
            >
              <Typography variant="body2">
                <strong>Liste A aktiviert:</strong> Parameter in der Liste sind mit einem blauen "A" in der Listen-Spalte (ganz links) markiert. Klicken Sie auf das "A", um den Parameter zu entfernen. Klicken Sie auf den Listen-Button bei Parametern, um sie{' '}
                {parameterLists.find(l => l.id === activeWorkingList[0])?.isPrivate
                  ? `direkt hinzuzufügen.`
                  : `vorzuschlagen (erfordert Approval).`
                }
              </Typography>
            </Alert>
          )}

          {activeWorkingList.length === 2 && (
            <Alert
              severity="success"
              sx={{ mt: 2 }}
              icon={<CheckCircleIcon />}
            >
              <Typography variant="body2">
                <strong>Listen-Vergleich aktiviert (A / B):</strong> Nutzen Sie die Filter-Chips oben, um Parameter zu filtern. In der Listen-Spalte (ganz links) sehen Sie farbige Kreise mit A und/oder B. Klicken Sie auf einen Kreis, um den Parameter aus der entsprechenden Liste zu entfernen.
              </Typography>
            </Alert>
          )}
        </Paper>

        {/* Results Table */}
        <DataCatalogTable
          parameters={parametersWithFavorites}
          nameView={nameView}
          expandedRows={expandedRows}
          onRowClick={handleRowClick}
          onFavoriteToggle={handleFavoriteToggle}
          handleOpenListDialog={handleOpenListDialog}
          orderBy={orderBy}
          order={order}
          handleRequestSort={handleRequestSort}
          activeWorkingList={activeWorkingList}
          parameterLists={parameterLists}
          getParameterListStatus={getParameterListStatus}
          isParameterInAnyList={isParameterInAnyList}
          handleRemoveFromList={handleRemoveFromList}
          handleAddToList={handleAddToList}
          allTagsWithStats={allTagsWithStats}
          getIconComponent={getIconComponent}
          getPrimaryDescription={getPrimaryDescription}
          subtlePulse={subtlePulse}
        />

        {/* Tag Management Dialog */}
        <TagManagementDialog
          open={tagDialogOpen}
          onClose={() => setTagDialogOpen(false)}
          allTagsWithStats={allTagsWithStats}
          currentUser={currentUser}
          getIconComponent={getIconComponent}
          handleMakeTagPublic={handleMakeTagPublic}
        />

        {/* Set Management Dialog */}
        <SetManagementDialog
          open={setManagementDialogOpen}
          onClose={() => setSetManagementDialogOpen(false)}
          savedSets={savedSets}
          activeSetTab={activeSetTab}
          setActiveSetTab={setActiveSetTab}
          handleLoadSet={handleLoadSet}
          getSetTypeBadge={getSetTypeBadge}
        />

        {/* Tag Deletion Request Dialog */}
        <TagDeletionRequestDialog
          open={deletionRequestDialog.open}
          onClose={() => setDeletionRequestDialog({ open: false, tag: null })}
          tag={deletionRequestDialog.tag}
          onConfirm={handleConfirmDeletionRequest}
        />

        {/* Parameter List Dialog - Add parameter to lists */}
        <WorkingListDialog
          open={listDialogOpen}
          onClose={() => setListDialogOpen(false)}
          selectedParameterId={selectedParameterForList}
          selectedParameter={mockParameters.find(p => p.id === selectedParameterForList)}
          parameterLists={parameterLists}
          currentUser={currentUser}
          isParameterInList={isParameterInList}
          handleToggleParameterInList={handleToggleParameterInList}
          hasParameterPendingApproval={hasParameterPendingApproval}
          setSnackbar={setSnackbar}
        />

        {/* List Management Dialog - Manage all lists */}
        <ListManagementDialog
          open={listManagementDialogOpen}
          onClose={() => setListManagementDialogOpen(false)}
          parameterLists={parameterLists}
          setParameterLists={setParameterLists}
          currentUser={currentUser}
          activeWorkingList={activeWorkingList}
          setActiveWorkingList={setActiveWorkingList}
          setSnackbar={setSnackbar}
        />

        {/* List Addition Request Confirmation Dialog */}
        <ListAddRequestDialog
          open={listAddRequestDialog.open}
          onClose={() => setListAddRequestDialog({ open: false, list: null, parameter: null })}
          list={listAddRequestDialog.list}
          parameterId={listAddRequestDialog.parameter}
          onConfirm={handleConfirmListAdditionRequest}
        />

        {/* AI Assistant Modal */}
        <AIAssistantDialog
          open={aiAssistantOpen}
          onClose={() => setAiAssistantOpen(false)}
          studyDescription={studyDescription}
          setStudyDescription={setStudyDescription}
          studyType={studyType}
          setStudyType={setStudyType}
          specialty={specialty}
          setSpecialty={setSpecialty}
          aiSuggestions={aiSuggestions}
          setAiSuggestions={setAiSuggestions}
          selectedSuggestions={selectedSuggestions}
          setSelectedSuggestions={setSelectedSuggestions}
          handleGenerateSuggestions={handleGenerateSuggestions}
          handleApplySuggestionsAsFilter={handleApplySuggestionsAsFilter}
          isGeneratingSuggestions={isGeneratingSuggestions}
        />

        {/* Toast Notification */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={(event, reason) => {
            // Ignore clickaway - only close on timeout or manual close
            if (reason === 'clickaway') {
              return;
            }
            setSnackbar({ ...snackbar, open: false });
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
};

export default DataCatalog;
